import React, { useEffect, useState, useCallback } from "react";
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from "moment";
import 'react-big-calendar/lib/css/react-big-calendar.css';
import "react-datepicker/dist/react-datepicker.css";
import { connect } from "react-redux";
import Loading from "../Loading";
import { Button, List, Select } from "semantic-ui-react";
import PayCalendarCareTopMenus from "../PayCalendarCareTopMenus";
import PreviousPayDataDetails from "../PreviousPayDataDetails";
import MonthYearJumper from "./MonthYearJumper";

// Hooks
import { useCalendarNavigation } from './hooks/useCalendarNavigation';
import { usePayDataFetching } from './hooks/usePayDataFetching';
import { useDoctorSelection } from './hooks/useDoctorSelection';
import { useCalendarEvents } from './hooks/useCalendarEvents';
import { usePreviousPayModal } from './hooks/usePreviousPayModal';
import { getExcludedCodes } from '../../redux/modules/procCodes';

const localizer = momentLocalizer(moment);

const CustomToolbar = ({ label, onNavigate, currentDate, navigateToDate }) => {
    return (
        <div className='rbc-toolbar'>
            {/* Same cdp-month-navigator markup/classes as CdpReports' month navigator, instead of
                relying on rbc-btn-group's position-based CSS (:first-child/nth-child), which breaks
                as soon as the button order in this toolbar changes. margin-right: auto on this
                wrapper (instead of RBC's flex-grow .rbc-toolbar-label, which no longer wraps the
                label directly) keeps Today+arrows pinned left and the jumper pinned right. */}
            <span className='cdp-pay-toolbar-left'>
                <Button type='button' basic size='tiny' onClick={() => onNavigate('TODAY')}>Today</Button>
                <span className='cdp-month-navigator'>
                    <button type='button' className='cdp-month-navigator__arrow' onClick={() => onNavigate('PREV')} aria-label='Previous month'>&#8249;</button>
                    <span className='cdp-month-navigator__label'>{label}</span>
                    <button type='button' className='cdp-month-navigator__arrow' onClick={() => onNavigate('NEXT')} aria-label='Next month'>&#8250;</button>
                </span>
            </span>
            <span className='rbc-btn-group'>
                <MonthYearJumper currentDate={currentDate} navigateToDate={navigateToDate} />
            </span>
        </div>
    );
};

const CdpPayCalendar = ({ match, saved, user, payData, historyReportData, isLoad, dispatch, practiceListArray, billingData, billingParams }) => {

    // Custom hooks
    const { showModal: prePayDetailsModal, openModal: openPreviousPayModal, closeModal: closePreviousPayModal } = usePreviousPayModal();

    const { fetchPayData } = usePayDataFetching(user, null, dispatch, saved);

    const { currentDate, navigateToDate, handleMonthChange } = useCalendarNavigation(
        match,
        user,
        dispatch,
        saved,
        fetchPayData
    );

    const { selectedAbbr, doctorOptions, handleDoctorChange, changeDoctorAbbrAction } = useDoctorSelection(
        user,
        match,
        currentDate,
        dispatch,
        (view) => navigateToDate(currentDate, view)
    );

    const {
        calendarEvents,
        providerPayByDoctor,
        earningsArray,
        dateKeyObjects
    } = useCalendarEvents(payData, user);

    // Initialize doctor abbr from URL on mount
    useEffect(() => {
        const abbr = match?.params?.abbr;
        if (abbr) {
            changeDoctorAbbrAction(abbr);
        }
    }, []);

    // Load excluded procedure codes into window cache on mount (redux pattern)
    useEffect(() => {
        if (dispatch) {
            dispatch(getExcludedCodes()).then(res => {
                try {
                    const codes = (res && res.excludedCodes) ? res.excludedCodes.map(c => (c || '').toString().trim().toUpperCase()) : [];
                    if (codes && codes.length) {
                        window.__EXCLUDED_PROC_CODES__ = codes;
                        try { window.dispatchEvent(new Event('excludedProcCodesUpdated')); } catch (e) { /* ignore */ }
                    }
                } catch (e) {
                    /* ignore */
                }
            }).catch(() => { });
        }
    }, []);

    // Set selected doctor name for display
    if (user && user.UdaDrList) {
        let selectedDoctor = user.UdaDrList;
        if (user.drList && user.drList[2] && Object.keys(user.drList[2]).length && user.drList[2][user.UdaDrList.abbr]) {
            selectedDoctor = user.drList[2][user.UdaDrList.abbr];
        }
        user.UdaDrList.selectedDoctor = selectedDoctor.firstName + ' ' + selectedDoctor.lastName;
    }

    return (
        <React.Fragment>
            <div className="calendar-container">
                {isLoad && <Loading />}

                {prePayDetailsModal && (
                    <PreviousPayDataDetails
                        providerPayByPractice={providerPayByDoctor}
                        payData={historyReportData.status}
                        handlePreviousPayDetailsModal={closePreviousPayModal}
                    />
                )}

                <div className="row d-flex align-items-center px30 px-sm-20">
                    <div className="col-lg-10 col-md-12 col-sm-12 px-0">
                        <div className="float-lg-left float-sm-left float-md-left mb10 careTop">
                            <PayCalendarCareTopMenus
                                user={user}
                                payData={payData}
                                historyReportData={historyReportData}
                                isLoad={isLoad}
                                match={match}
                                saved={saved}
                                dispatch={dispatch}
                                practiceListArray={practiceListArray}
                                billingData={billingData}
                                billingParams={billingParams}
                                dateKeyObjects={dateKeyObjects}
                                providerPayByPractice={providerPayByDoctor}
                                earningsArray={earningsArray}
                                startDate={currentDate}
                                prevPayTotal={historyReportData?.summary?.difference?.total}
                                handlePreviousPayDetailsModal={openPreviousPayModal}
                            />
                        </div>
                    </div>
                    <div className="col-lg-2 col-md-12 col-sm-12 px-0">
                        <div className="float-lg-right float-sm-left float-md-left mb10 careTop">
                            <List horizontal className="topValues test">
                                <List.Item>
                                    {user.isAdmin && doctorOptions.length ? (
                                        <Select
                                            search
                                            value={selectedAbbr}
                                            onChange={handleDoctorChange}
                                            placeholder='Select Doctor'
                                            options={doctorOptions}
                                        />
                                    ) : ''}
                                </List.Item>
                            </List>
                        </div>
                    </div>
                </div>
                <div className="ui card">
                    <div className="content">
                        <Calendar
                            localizer={localizer}
                            events={calendarEvents}
                            startAccessor="start"
                            endAccessor="end"
                            onNavigate={handleMonthChange}
                            popup={false}
                            showMultiDayTimes={false}
                            showAllEvents={true}
                            defaultDate={new Date(moment(currentDate).startOf('month'))}
                            components={{
                                toolbar: (props) => <CustomToolbar {...props} currentDate={currentDate} navigateToDate={navigateToDate} />
                            }}
                        />
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default connect(
    state => ({
        user: state.get('auth').get('user'),
        billingParams: state.get('billing').get('billingParams'),
        historyReportData: state.get('payReports').get('historyReportData'),
        billingData: state.get('billing').get('billingData'),
        payData: state.get('payReports').get('billingData'),
        isLoad: state.get('payReports').get('isLoad'),
        loadErr: state.get('payReports').get('loadErr'),
        practiceListArray: state.get('practices').get('practiceListArray'),
    })
)(CdpPayCalendar);
